import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import StationCandidatesApi from '../lib/api/StationCandidatesApi';
import { apiErrorMessage } from '../helpers/api';
import { stableStringify, toRequestParams } from '../lib/stationCandidates/searchParams';
import useDebouncedValue from './useDebouncedValue';

/**
 * React Query bindings for the candidate sweep.
 *
 * Two things drive the shape of this file.
 *
 * FIRST: THE ESTIMATE QUERY KEY MUST COVER EVERY PARAM THAT CHANGES THE
 * RESULT. This page exists to show money before it is spent, so a stale cost
 * is the worst failure it can have: an operator edits a filter, the panel
 * looks live, and the number on screen belongs to the previous settings.
 * Rather than hand-listing the params in the key (where the next added param
 * gets forgotten), the key carries a stable serialisation of the ENTIRE
 * request — bbox and params together — so coverage is structural rather than
 * remembered.
 *
 * SECOND: THE ESTIMATE AND THE SWEEP MUST BE QUOTED FROM IDENTICAL PARAMS, or
 * the quote and the bill diverge. Both paths therefore run their params
 * through `toRequestParams` here, in one place, instead of trusting each call
 * site to normalise identically. `toRequestParams` is idempotent, so a caller
 * that already normalised loses nothing — and it is what omits `minRating` /
 * `minUserRatingCount` when unset, which matters because `minRating: 0` is a
 * real filter rather than a no-op.
 *
 * The params module therefore lives in `lib/stationCandidates/`, beside the api
 * client that posts its output, rather than under the page that renders it: it
 * owns the wire key names precisely because `ValidationPipe({ whitelist: true })`
 * silently strips anything else, so it is shared infrastructure that both this
 * file and the panel consume. A second normaliser living here would be exactly
 * the duplication that lets the two drift.
 *
 * Mutations follow `useStationMutations`' idiom: they resolve `{ success, ... }`
 * and never throw, so call sites can `const { success } = await ...` and toast
 * on failure. `error` carries `apiErrorMessage`, because `ValidationPipe`
 * returns `message` as an ARRAY of field errors and `error.message` alone is
 * "Request failed with status code 400".
 */

/**
 * `stableStringify` is imported from the params module rather than defined here.
 *
 * It is needed twice over in this file: as a query key (an unsorted stringify
 * would treat a re-ordered params object as a different key and refetch for
 * nothing) and as the debounce input (debouncing a fresh object literal would
 * restart the timer on every render and the request would never fire). The
 * station builder page needs the SAME serialisation to tell whether the params
 * on screen still describe the sweep that was billed, and two copies of it is
 * exactly how that check and this cache key would drift apart.
 */

/** Long enough to swallow a rectangle drag or a burst of keystrokes in a number field. */
export const ESTIMATE_DEBOUNCE_MS = 400;

/**
 * What a sweep of `bbox` under `params` would cost. Spends nothing, and never
 * throws over the cap — it reports `overCap` plus `overCapReason`, which is
 * why the UI always estimates before offering Generate.
 *
 * Disabled without a bbox: the four bbox scalars are required and un-defaulted
 * server-side, so firing without one is a guaranteed 400.
 *
 * `enabled` is the caller's veto for the OTHER guaranteed-400 shapes, which
 * this hook cannot see. The one that exists today: `laneIds` is required
 * whenever `corridorFilterEnabled` is true, and the builder page's lane
 * selection is empty both while the city's lanes are still loading and when
 * the operator has unticked every lane. Those are the same request as far as
 * this hook is concerned but completely different things to say, and the
 * estimate fires on a debounce off every param change — so without a veto it
 * would spend a doomed round-trip and paint a 400 over a page that is merely
 * still loading. Defaults to true, so callers that cannot be in that state
 * pass nothing.
 *
 * Debounced because both inputs change continuously under the operator's hand
 * (dragging the rectangle, typing in a radius field) and each fire is a real
 * request. The debounce is on the SERIALISED request, so the timer restarts
 * only when the request actually differs.
 */
export const useCandidateEstimate = (
  { bbox, params } = {},
  { debounceMs = ESTIMATE_DEBOUNCE_MS, enabled = true } = {},
) => {
  const requestJson = stableStringify({
    bbox: bbox ?? null,
    params: toRequestParams(params ?? {}),
  });

  const debouncedJson = useDebouncedValue(requestJson, debounceMs);
  const request = useMemo(() => JSON.parse(debouncedJson), [debouncedJson]);

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    // The whole request is in the key, so no param can change the result
    // without changing the key. Do not narrow this to a hand-written subset.
    queryKey: ['station-candidates', 'estimate', debouncedJson],
    queryFn: () => StationCandidatesApi.estimateCandidates({ bbox: request.bbox, params: request.params }),
    enabled: !!request.bbox && enabled,
    keepPreviousData: false,
  });

  return {
    estimate: data ?? null,
    // A pending debounce is still "working" from the operator's point of view;
    // reporting idle there is what makes a stale number look authoritative.
    // Vetoed is NOT working, though: react-query reports a disabled query as
    // `isLoading`, and forwarding that would leave "Pricing this area…" on
    // screen forever beside the caller's own explanation of why it is not.
    isLoading: enabled && (isLoading || (!!bbox && debouncedJson !== requestJson)),
    isFetching,
    error,
    refetch,
  };
};

/**
 * Runs the sweep. Billable, and 400s over the cell/request cap, so gate the
 * call on the estimate's `overCap`.
 *
 * `stats.dropped` is a `Record<string, number>` whose keys appear only when
 * that reason fired — it is passed through untouched, never normalised into
 * fixed fields, or a newly added backend reason becomes invisible.
 */
export const useCandidateSweep = () => {
  const mutation = useMutation({
    mutationFn: ({ bbox, params }) =>
      StationCandidatesApi.generateCandidates({ bbox, params: toRequestParams(params ?? {}) }),
  });

  const runSweep = async ({ bbox, params } = {}) => {
    try {
      const { candidates, stats } = await mutation.mutateAsync({ bbox, params });
      return { success: true, candidates: candidates ?? [], stats: stats ?? null };
    } catch (error) {
      return { success: false, candidates: [], stats: null, error: apiErrorMessage(error) };
    }
  };

  return { runSweep, isSweeping: mutation.isLoading };
};

/**
 * Records vetoed candidates so later sweeps never re-propose them (they come
 * back as `dropped.previouslyRejected`). Max 200 rows per call — chunking is
 * the caller's job, since it also has to report per-chunk outcomes.
 */
export const useRecordRejections = () => {
  const mutation = useMutation({
    mutationFn: ({ rejections }) => StationCandidatesApi.recordRejections({ rejections }),
  });

  const recordRejections = async ({ rejections } = {}) => {
    try {
      const data = await mutation.mutateAsync({ rejections });
      return { success: true, recorded: data?.recorded ?? 0 };
    } catch (error) {
      return { success: false, recorded: 0, error: apiErrorMessage(error) };
    }
  };

  return { recordRejections, isRecording: mutation.isLoading };
};

/**
 * Creates approved candidates. Max 100 rows per call.
 *
 * A 2xx HERE IS NOT FULL SUCCESS: the endpoint returns 201 even when rows
 * failed, so `results` / `createdCount` / `failedCount` are passed through
 * untouched and `success` means only "the request itself went through".
 * Callers must read `failedCount` before reporting to the operator.
 *
 * `onSuccess` invalidates `['stations']` — a prefix match, catching every
 * paged/filtered entry — so the phase-3 stations list shows the new rows.
 */
export const useBatchCreateStations = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({ stations }) => StationCandidatesApi.batchCreateStations({ stations }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stations'] });
    },
  });

  const batchCreateStations = async ({ stations } = {}) => {
    try {
      const data = await mutation.mutateAsync({ stations });
      return {
        success: true,
        results: data?.results ?? [],
        createdCount: data?.createdCount ?? 0,
        failedCount: data?.failedCount ?? 0,
      };
    } catch (error) {
      return {
        success: false,
        results: [],
        createdCount: 0,
        failedCount: 0,
        error: apiErrorMessage(error),
      };
    }
  };

  return { batchCreateStations, isCreating: mutation.isLoading };
};

// Named exports only, deliberately: this file holds four independent hooks
// rather than one, and the `src/hooks` barrel re-exports them individually.
// (A default object here also trips `import/no-anonymous-default-export`.)
